<script setup lang="ts">
import { ref, computed, defineAsyncComponent } from 'vue'
import axios from 'axios'
import DataTable from './datatable/DataTable.vue'
const GefahrstoffForm = defineAsyncComponent(() => import('./GefahrstoffForm.vue'))
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Glasses, AlertTriangle, Wind, SprayCan, Biohazard, X, FileSpreadsheet, Loader2, Copy, Download, History } from 'lucide-vue-next'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useToast } from '@/components/ui/toast'
import { generateExcel, get_data_attr, optionLabel } from '@/utils/globals'
import { useI18n } from 'vue-i18n'
import { useViewStore } from '@/stores/viewStore'
import DangeroursMaterialsLogo from '@/components/images/biohazard.svg'

const { t } = useI18n()
const { toast } = useToast()
const viewStore = useViewStore()
const specsInfo = get_data_attr('specs')
const userInfo = get_data_attr('user')

const selectedGefahrstoff = ref<any | null>(null)
const detailLoading = ref(false)
const detailError = ref('')
const emkgAssessments = ref<any[]>([])
const emkgHistoryOpen = ref(false)
const emkgHistoryLoading = ref(false)
const detailCache = new Map<string, any>()
let detailRequestSequence = 0
const gefahrstoffeTable = ref<any | null>(null)
const isExporting = ref(false)
const formMode = ref<'new' | 'edit' | 'duplicate' | null>(null)
const editingGefahrstoffId = ref<number | undefined>(undefined)
const duplicateGefahrstoffId = ref<number | undefined>(undefined)
const savingRowId = ref<string | number | null>(null)
const pendingRollbackRow = ref<any | null>(null)

function detailCacheKey(row: any) {
  return `${row?.id}:${row?.updated_at || ''}`
}

async function setSelectedGefahrstoff(row: any) {
  const requestId = ++detailRequestSequence
  detailError.value = ''
  if (!row?.id) {
    selectedGefahrstoff.value = null
    detailLoading.value = false
    return
  }

  selectedGefahrstoff.value = row
  void loadEmkgAssessments(row.id)
  const cacheKey = detailCacheKey(row)
  const cached = detailCache.get(cacheKey)
  if (cached) {
    selectedGefahrstoff.value = cached
    detailLoading.value = false
    performance.mark('gefahrstoffe-detail-ready')
    return
  }

  detailLoading.value = true
  try {
    const response = await axios.get(`/gefahrstoffe/${row.id}.json`)
    if (requestId !== detailRequestSequence) return
    detailCache.set(cacheKey, response.data)
    selectedGefahrstoff.value = response.data
    performance.mark('gefahrstoffe-detail-ready')
  } catch (error: any) {
    if (requestId !== detailRequestSequence) return
    detailError.value = error?.response?.data?.error || error?.response?.statusText || 'Details konnten nicht geladen werden.'
  } finally {
    if (requestId === detailRequestSequence) detailLoading.value = false
  }
}

async function loadEmkgAssessments(gefahrstoffId: number) {
  emkgHistoryLoading.value = true
  try {
    const response = await axios.get(`/gefahrstoffe/${gefahrstoffId}/emkg_assessments.json`)
    if (String(selectedGefahrstoff.value?.id) === String(gefahrstoffId)) emkgAssessments.value = response.data || []
  } catch {
    if (String(selectedGefahrstoff.value?.id) === String(gefahrstoffId)) emkgAssessments.value = []
  } finally {
    emkgHistoryLoading.value = false
  }
}

const latestEmkgAssessment = computed(() => emkgAssessments.value[0] || null)
const emkgAreas = [
  { key: 'skin', label: 'Haut' },
  { key: 'inhalation', label: 'Einatmen' },
  { key: 'eyes', label: 'Augen' },
  { key: 'fire', label: 'Brand und Explosion' },
  { key: 'storage', label: 'Lagern' },
]

function emkgLevelClass(level: string) {
  if (level === 'NA') return 'border-slate-300 bg-slate-100 text-slate-700'
  if (level === 'G') return 'border-green-200 bg-green-50 text-green-900'
  if (level === 'M1') return 'border-lime-200 bg-lime-50 text-lime-900'
  if (level === 'M2') return 'border-blue-200 bg-blue-50 text-blue-900'
  if (level === 'M3') return 'border-amber-200 bg-amber-50 text-amber-900'
  return 'border-red-200 bg-red-50 text-red-900'
}

function handleGefahrstoffDeleted(rowId: string | number) {
  for (const key of detailCache.keys()) {
    if (key.startsWith(`${rowId}:`)) detailCache.delete(key)
  }
  if (String(selectedGefahrstoff.value?.id) === String(rowId)) {
    selectedGefahrstoff.value = null
  }
}

const formSheetOpen = computed({
  get: () => formMode.value !== null,
  set: (open: boolean) => {
    if (!open) closeFormSheet()
  }
})

const formSheetTitle = computed(() => {
  if (formMode.value === 'duplicate') return 'Gefahrstoff duplizieren'
  return formMode.value === 'edit' ? 'Gefahrstoff bearbeiten' : 'Neuer Gefahrstoff'
})

const formKey = computed(() => {
  if (formMode.value === 'duplicate') return `duplicate-${duplicateGefahrstoffId.value}`
  return formMode.value === 'edit' ? `edit-${editingGefahrstoffId.value}` : 'new'
})

function openCreateSheet() {
  if (savingRowId.value) return
  formMode.value = 'new'
  editingGefahrstoffId.value = undefined
  duplicateGefahrstoffId.value = undefined
  pendingRollbackRow.value = null
}

function openEditSheet(row: any) {
  if (savingRowId.value || !row?.id) return
  formMode.value = 'edit'
  editingGefahrstoffId.value = Number(row.id)
  duplicateGefahrstoffId.value = undefined
  pendingRollbackRow.value = null
}

function openDuplicateSheet(row: any) {
  if (savingRowId.value || !row?.id) return
  formMode.value = 'duplicate'
  editingGefahrstoffId.value = undefined
  duplicateGefahrstoffId.value = Number(row.id)
  pendingRollbackRow.value = null
}

function closeFormSheet() {
  if (savingRowId.value) return
  formMode.value = null
  editingGefahrstoffId.value = undefined
  duplicateGefahrstoffId.value = undefined
  pendingRollbackRow.value = null
}

function updateSelectedGefahrstoff(rowId: string | number, row: any) {
  if (!selectedGefahrstoff.value || selectedGefahrstoff.value.id !== rowId) return
  selectedGefahrstoff.value = { ...selectedGefahrstoff.value, ...row }
}

function handleFormSaveStart(payload: any) {
  const isEdit = payload?.isEditMode && payload?.id != null
  const optimisticRow = payload?.row || {}

  if (isEdit) {
    const rowId = payload.id
    pendingRollbackRow.value = gefahrstoffeTable.value?.snapshotRow(rowId) || null
    savingRowId.value = rowId
    gefahrstoffeTable.value?.optimisticReplaceRow(rowId, optimisticRow)
    updateSelectedGefahrstoff(rowId, optimisticRow)
    return
  }

  const tempId = `new-gefahrstoff-${Date.now()}`
  pendingRollbackRow.value = null
  savingRowId.value = tempId
  gefahrstoffeTable.value?.prependTemporaryRow({
    ...optimisticRow,
    id: tempId,
    isNew: true
  })
}

function handleFormSaved(payload: any) {
  const row = payload?.row
  const rowId = savingRowId.value || payload?.id || row?.id

  if (row && rowId != null) {
    for (const key of detailCache.keys()) {
      if (key.startsWith(`${rowId}:`)) detailCache.delete(key)
    }
    gefahrstoffeTable.value?.replaceLocalRow(rowId, row)
    if (selectedGefahrstoff.value && (selectedGefahrstoff.value.id === rowId || selectedGefahrstoff.value.id === row.id)) {
      selectedGefahrstoff.value = row
    }
    if (formMode.value === 'duplicate') {
      selectedGefahrstoff.value = row
    }
  }

  savingRowId.value = null
  pendingRollbackRow.value = null
  formMode.value = null
  editingGefahrstoffId.value = undefined
  duplicateGefahrstoffId.value = undefined
}

function handleFormSaveError() {
  const rowId = savingRowId.value
  if (rowId != null) {
    gefahrstoffeTable.value?.rollbackLocalRow(rowId, pendingRollbackRow.value)
    if (pendingRollbackRow.value && selectedGefahrstoff.value?.id === rowId) {
      selectedGefahrstoff.value = pendingRollbackRow.value
    }
  }
  savingRowId.value = null
  pendingRollbackRow.value = null
}

function openBatchUpdate() {
  viewStore.setActionView('registry.gefahrstoffe.batch')
}

async function exportGefahrstoffe() {
  if (isExporting.value) return

  const params = { ...(gefahrstoffeTable.value?.currentQueryParams?.() || {}) }
  delete params.page
  delete params.per_page
  delete params.reset_filter
  delete params.reset_filter_button

  const exportColumns = (gefahrstoffeTable.value?.currentExportColumns?.() || [])
    .filter((column: any) => column?.key)

  await generateExcel(axios, '/gefahrstoffe/export.xlsx', toast, isExporting, {
    method: 'post',
    data: {
      ...params,
      columns: exportColumns,
    },
  })
}

function getNested(obj: any, path: string) {
  if (!obj || !path) return undefined
  const parts = path.split('.')
  return parts.reduce((acc, key) => (acc ? acc[key] : undefined), obj)
}

function formatValue(column: any, value: any) {
  if (value === null || value === undefined || value === '') return '–'

  // Many-to-many collections
  if (column.type === 'many') {
    const list = Array.isArray(value) ? value : Object.values(value)
    if (!list.length) return '–'
    return list.map((item: any) => optionLabel(column.options || [], item)).join(', ')
  }

  // Select / multi-select
  if (column.type === 'select' || column.type === 'multi-select') {
    return optionLabel(column.options || [], value)
  }

  // Boolean
  if (column.type === 'boolean') {
    return value ? t('view.yes') : t('view.no')
  }

  // Dates
  if (column.type === 'date' && typeof value === 'string') {
    return value.split('T')[0]
  }

  // Fallback
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const gefahrstoffeColumns = computed(() => specsInfo?.gefahrstoffe || [])

function getColumn(key: string) {
  const direct = gefahrstoffeColumns.value.find((c: any) => c.key === key)
  if (direct) return direct

  // Backend specs still define Standort as `standort_id`, while detail payload exposes `standort` label text.
  if (key === 'standort') {
    return gefahrstoffeColumns.value.find((c: any) => c.key === 'standort_id')
  }

  return undefined
}

function fieldValue(key: string) {
  if (!selectedGefahrstoff.value) return undefined
  const col = getColumn(key)
  const value = getNested(selectedGefahrstoff.value, key)
  return col ? formatValue(col, value) : (value ?? '–')
}

// Section 1: Identification & hazard info (UN-Nr., EAK, H/P/EUH, Signalwort, CAS, Kommentar, Flammpunkt, WGK, Lagerklasse, GHS)
const section1Keys = [
  'un_number',
  'eak_number',
  'h_phrases',
  'p_phrases',
  'eurh_phrases',
  'h_phrases_signal_word',
  'cas_numbers',
  'flammpunkt',
  'water_hazard_class',
  'inventory_class',
  'comment',
]

// Section 2: Verwendung & Lagerung
const section2Keys = [
  'active',
  'use',
  'use_start',
  'use_end',
  'use_duration_periodicity',
  'workspace',
  'number_of_workers',
  'standort',
  'use_location_or_machinery',
  'inventory_space_name',
  'inventory_space_area_name',
  'inventory_space_type',
  'inventory_size',
  'yearly_usage',
  'extinguishing_agent_suitable',
  'extinguishing_agent_unsuitable',
  'inventory_conditions',
  'gebinde_number',
]

// Section 3: Physikalisch-chemische Eigenschaften
const section3Keys = [
  'physical_state_at_room_temperature',
  'form',
  'color',
  'odor',
  'solubility_in_water',
  'melting_point',
  'boiling_point',
  'ignition_temperature',
  'autoignition_temperature',
  'decomposition_point',
  'density',
  'viscosity',
  'vapor_pressure',
  'relative_vapor_density',
  'ph_value',
  'lower_explosion_limit_vol_percent',
  'upper_explosion_limit_vol_percent',
  'reactivity',
  'stability',
]

const ghsPictogramUrls = computed(() => {
  // Prefer pictograms explicitly assigned via gefahrstoffe_ghs (backend: ghs_pictograms_urls)
  const directUrls = getNested(selectedGefahrstoff.value, 'ghs_pictograms_urls')
  if (Array.isArray(directUrls) && directUrls.length) return directUrls

  // Fallback for endpoints that embed ghs objects
  const ghs = getNested(selectedGefahrstoff.value, 'ghs')
  if (Array.isArray(ghs) && ghs.length) {
    return ghs
      .map((g: any) => g?.pictogram_url)
      .filter((u: any) => typeof u === 'string' && u.length > 0)
  }

  return []
})
</script>

<template>
  <div class="flex h-screen w-full">
    <div class="flex w-full p-4 gap-4">
      <!-- Left: Gefahrstoffe table -->
      <div
        class="flex-1 transition-[max-width] duration-300 ease-in-out"
        :class="selectedGefahrstoff ? 'max-w-[50%]' : 'max-w-full'"
      >
        <DataTable
          ref="gefahrstoffeTable"
          label="view.gefahrstoffe"
          labelSingular="view.gefahrstoff"
          :logo="null"
          endpoint="gefahrstoffe"
          :columns="specsInfo['gefahrstoffe']"
          :enableDetail="false"
          :remove-deleted-row-locally="true"
          @request-add="openCreateSheet"
          @request-edit="openEditSheet"
          @row-deleted="handleGefahrstoffDeleted"
        >
          <template #logo>
            <DangeroursMaterialsLogo class="w-8 h-8" />
          </template>

          <template #extraactions="{ row }">
            <Button
              :id="'gefahrstoff-duplicate-' + row.id"
              title="Duplizieren"
              size="icon"
              variant="ghost"
              class="text-black-600 hover:bg-blue-50"
              @click.stop
              @click="() => openDuplicateSheet(row)"
            >
              <Copy class="w-4 h-4" />
            </Button>
            <Button
              :id="'gefahrstoff-detail-' + row.id"
              :title="t('view.btn_detail')"
              size="icon"
              variant="ghost"
              class="text-black-600 hover:bg-blue-50"
              @click="() => setSelectedGefahrstoff(row)"
            >
              <Glasses class="w-4 h-4" />
            </Button>
          </template>
          <template #extraglobalactions>
            <Button
              v-if="userInfo.is_hoeppner"
              variant="outline"
              :disabled="isExporting"
              @click="exportGefahrstoffe"
            >
              <Loader2 v-if="isExporting" class="mr-2 h-4 w-4 animate-spin" />
              <FileSpreadsheet v-else class="mr-2 h-4 w-4" />
              Excel Export
            </Button>
            <Button
              v-if="userInfo.is_hoeppner"
              variant="outline"
              @click="openBatchUpdate"
            >
              Batch-Update Gefahrstoffe
            </Button>
          </template>
        </DataTable>
      </div>

      <!-- Right: Detail sheet -->
      <div class="flex-1" v-if="selectedGefahrstoff">
        <Card class="h-full overflow-auto flex flex-col">
          <div class="flex items-center justify-between p-4 border-b bg-gray-50">
            <div class="flex items-center gap-2">
              <h2 class="text-xl font-bold">
                {{ selectedGefahrstoff?.bezeichnung || selectedGefahrstoff?.name || selectedGefahrstoff?.id }}
              </h2>
              <Tooltip>
                <TooltipTrigger as-child>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    class="shrink-0 text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                    :title="t('view.close')"
                    @click="setSelectedGefahrstoff(null)"
                  >
                    <X class="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {{ t('view.close') }}
                </TooltipContent>
              </Tooltip>
            </div>
            <div class="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger as-child>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    class="shrink-0 text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                    title="Duplizieren"
                    @click="openDuplicateSheet(selectedGefahrstoff)"
                  >
                    <Copy class="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Duplizieren
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="inline-flex" :class="selectedGefahrstoff?.hazardous_material ? 'text-green-600' : 'text-gray-400'">
                    <AlertTriangle class="w-5 h-5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {{ selectedGefahrstoff?.hazardous_material ? 'Gefahrstoff' : 'Kein Gefahrstoff' }}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="inline-flex" :class="selectedGefahrstoff?.gas ? 'text-green-600' : 'text-gray-400'">
                    <Wind class="w-5 h-5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {{ selectedGefahrstoff?.gas ? 'Gas' : 'Kein Gas' }}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="inline-flex" :class="selectedGefahrstoff?.spray ? 'text-green-600' : 'text-gray-400'">
                    <SprayCan class="w-5 h-5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {{ selectedGefahrstoff?.spray ? 'Spray' : 'Kein Spray' }}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="inline-flex" :class="selectedGefahrstoff?.carcinogenic_material ? 'text-green-600' : 'text-gray-400'">
                    <Biohazard class="w-5 h-5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {{ selectedGefahrstoff?.carcinogenic_material ? 'Krebserzeugend' : 'Nicht Krebserzeugend' }}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <div v-if="detailLoading" class="flex flex-1 items-center justify-center p-8">
            <div class="flex items-center gap-3 text-sm text-gray-600">
              <Loader2 class="h-5 w-5 animate-spin" />
              Details werden geladen…
            </div>
          </div>
          <div v-else-if="detailError" class="m-4 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {{ detailError }}
          </div>
          <div v-else class="p-4 overflow-auto space-y-6 text-sm">
            <!-- Section 1: Kennzeichnung & Gefahren -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Kennzeichnung & Gefahren
              </h3>
              <div class="grid grid-cols-2 gap-x-6 gap-y-3">
                <div
                  v-for="key in section1Keys"
                  :key="key"
                  class="flex flex-col"
                >
                  <div class="font-semibold text-gray-700">
                    {{ getColumn(key)?.label ?? key }}
                  </div>
                  <div class="text-gray-900 break-words">
                    {{ fieldValue(key) }}
                  </div>
                </div>
              </div>
              <div class="flex flex-col mt-2">
                <div class="font-semibold text-gray-700 mb-2">
                  GHS-Piktogramme
                </div>
                <div v-if="ghsPictogramUrls.length" class="flex flex-wrap gap-2">
                  <img
                    v-for="(url, idx) in ghsPictogramUrls"
                    :key="idx"
                    :src="url"
                    alt="GHS pictogram"
                    class="w-12 h-12 object-contain"
                  />
                </div>
                <div v-else class="text-gray-900">
                  -
                </div>
              </div>
            </section>

            <!-- Section 2: Verwendung & Lagerung -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Verwendung & Lagerung
              </h3>
              <div class="grid grid-cols-2 gap-x-6 gap-y-3">
                <div
                  v-for="key in section2Keys"
                  :key="key"
                  class="flex flex-col"
                >
                  <div class="font-semibold text-gray-700">
                    {{ getColumn(key)?.label ?? key }}
                  </div>
                  <div class="text-gray-900 break-words">
                    {{ fieldValue(key) }}
                  </div>
                </div>
              </div>
            </section>

            <!-- Section 3: Physikalisch-chemische Eigenschaften -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Physikalisch-chemische Eigenschaften
              </h3>
              <div class="grid grid-cols-2 gap-x-6 gap-y-3">
                <div
                  v-for="key in section3Keys"
                  :key="key"
                  class="flex flex-col"
                >
                  <div class="font-semibold text-gray-700">
                    {{ getColumn(key)?.label ?? key }}
                  </div>
                  <div class="text-gray-900 break-words">
                    {{ fieldValue(key) }}
                  </div>
                </div>
              </div>
            </section>

            <!-- Section 4: Substitutionsprüfung -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Substitutionsprüfung
              </h3>
              <div
                v-if="selectedGefahrstoff?.substitutiontest"
                class="grid grid-cols-2 gap-x-6 gap-y-3 text-gray-900"
              >
                <template v-if="selectedGefahrstoff?.substitutiontest">
                  <div class="flex flex-col">
                    <div class="font-semibold text-gray-700">Nächste Prüfung</div>
                    <div>{{ selectedGefahrstoff.substitutiontest.next_check ? selectedGefahrstoff.substitutiontest.next_check.split('T')[0] : '–' }}</div>
                  </div>
                  <div class="flex flex-col">
                  </div>
                  <div class="flex flex-col">
                    <div class="font-semibold text-gray-700">Verantwortlich</div>
                    <div>{{ selectedGefahrstoff.substitutiontest.name_responsible || '–' }}</div>
                  </div>
                  <div class="flex flex-col">
                    <div class="font-semibold text-gray-700">E-Mail Verantwortlicher</div>
                    <div>{{ selectedGefahrstoff.substitutiontest.email_responsible || '–' }}</div>
                  </div>
                  <div class="flex flex-col">
                    <div class="font-semibold text-gray-700">E-Mail Abteilung</div>
                    <div>{{ selectedGefahrstoff.substitutiontest.email_department || '–' }}</div>
                  </div>
                  <div class="flex flex-col col-span-2">
                    <div class="font-semibold text-gray-700">Ergebnis</div>
                    <div class="break-words">{{ selectedGefahrstoff.substitutiontest.result || '–' }}</div>
                  </div>
                  <div class="flex flex-col col-span-2">
                    <div class="font-semibold text-gray-700">Dokument</div>
                    <a
                      v-if="selectedGefahrstoff.substitutiontest.substitutiontest_url"
                      :href="selectedGefahrstoff.substitutiontest.substitutiontest_url"
                      target="_blank"
                      rel="noopener"
                      download
                      class="mt-1 inline-flex w-fit items-center gap-2 text-blue-600 hover:underline"
                    >
                      <Download class="h-4 w-4" />
                      Substitutionsprüfung herunterladen
                    </a>
                    <span v-else class="text-gray-500">Kein Dokument verknüpft.</span>
                  </div>
                </template>
              </div>
              <p v-else class="text-gray-500">Keine Substitutionsprüfung hinterlegt.</p>
            </section>

            <!-- Section 4: Sicherheitsdatenblatt -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Sicherheitsdatenblatt
              </h3>
              <div
                v-if="selectedGefahrstoff?.datasheet"
                class="grid grid-cols-2 gap-x-6 gap-y-3 text-gray-900"
              >
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Nächste Prüfung</div>
                  <div>{{ selectedGefahrstoff.datasheet.next_check ? selectedGefahrstoff.datasheet.next_check.split('T')[0] : '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Hersteller</div>
                  <div>{{ selectedGefahrstoff.datasheet.manufacturer || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Verantwortlich</div>
                  <div>{{ selectedGefahrstoff.datasheet.name_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Verantwortlicher</div>
                  <div>{{ selectedGefahrstoff.datasheet.email_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Abteilung</div>
                  <div>{{ selectedGefahrstoff.datasheet.email_department || '–' }}</div>
                </div>
                <div class="flex flex-col col-span-2">
                  <a
                    v-if="selectedGefahrstoff.datasheet.datasheet_url"
                    :href="selectedGefahrstoff.datasheet.datasheet_url"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline"
                  >
                    Sicherheitsdatenblatt öffnen
                  </a>
                  <span v-else class="text-gray-500">Kein Dokument verknüpft.</span>
                </div>
              </div>
              <p v-else class="text-gray-500">Kein Sicherheitsdatenblatt hinterlegt.</p>
            </section>

            <!-- Section 5: GBU -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Gefährdungsbeurteilung
              </h3>
              <div
                v-if="selectedGefahrstoff?.gbu"
                class="grid grid-cols-2 gap-x-6 gap-y-3 text-gray-900"
              >
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Nächste Prüfung</div>
                  <div>{{ selectedGefahrstoff.gbu.next_check ? selectedGefahrstoff.gbu.next_check.split('T')[0] : '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Kennung</div>
                  <div>{{ selectedGefahrstoff.gbu.identifier || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Maßnahmen umgesetzt</div>
                  <div>{{ selectedGefahrstoff.gbu.measures_implemented ? 'Ja' : 'Nein' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Umsetzungsdatum</div>
                  <div>{{ selectedGefahrstoff.gbu.implementation_date ? selectedGefahrstoff.gbu.implementation_date.split('T')[0] : '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Wirksamkeitsprüfung durchgeführt</div>
                  <div>{{ selectedGefahrstoff.gbu.effectiveness_check_performed ? 'Ja' : 'Nein' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Durchführungsdatum</div>
                  <div>{{ selectedGefahrstoff.gbu.effectiveness_check_date ? selectedGefahrstoff.gbu.effectiveness_check_date.split('T')[0] : '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Verantwortlich</div>
                  <div>{{ selectedGefahrstoff.gbu.name_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Verantwortlicher</div>
                  <div>{{ selectedGefahrstoff.gbu.email_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Abteilung</div>
                  <div>{{ selectedGefahrstoff.gbu.email_department || '–' }}</div>
                </div>
              </div>
              <p v-else class="text-gray-500">Keine GBU hinterlegt.</p>

              <div v-if="emkgHistoryLoading" class="flex items-center gap-2 text-gray-500">
                <Loader2 class="h-4 w-4 animate-spin" /> EMKG-Bewertungen werden geladen…
              </div>
              <div v-else-if="latestEmkgAssessment" class="space-y-3 rounded-md border bg-slate-50 p-3">
                <div class="flex items-center justify-between gap-3">
                  <div>
                    <div class="font-semibold text-gray-800">Aktuelle EMKG-Bewertung</div>
                    <div class="text-xs text-gray-500">{{ latestEmkgAssessment.assessed_on }} · {{ latestEmkgAssessment.assessor_name || '–' }}</div>
                  </div>
                  <a v-if="latestEmkgAssessment.pdf_url" :href="latestEmkgAssessment.pdf_url" class="inline-flex h-9 w-9 items-center justify-center rounded-md border bg-white text-blue-700" title="EMKG-PDF herunterladen">
                    <Download class="h-4 w-4" />
                  </a>
                </div>
                <div class="grid gap-2 sm:grid-cols-2">
                  <div v-for="area in emkgAreas" :key="area.key" class="flex items-center justify-between gap-2">
                    <span>{{ area.label }}</span>
                    <span class="text-right">
                      <span class="inline-block rounded border px-2 py-1 text-xs font-semibold" :class="emkgLevelClass(latestEmkgAssessment.snapshot.effective[area.key].level)">
                        {{ latestEmkgAssessment.snapshot.effective[area.key].level }}<span v-if="latestEmkgAssessment.snapshot.effective[area.key].manual"> · manuell</span>
                      </span>
                      <span v-if="latestEmkgAssessment.snapshot.effective[area.key].reason" class="mt-1 block max-w-64 text-xs text-slate-500">
                        {{ latestEmkgAssessment.snapshot.effective[area.key].reason }}
                      </span>
                    </span>
                  </div>
                </div>
                <Button v-if="emkgAssessments.length > 1" type="button" size="sm" variant="outline" @click="emkgHistoryOpen = !emkgHistoryOpen">
                  <History class="mr-2 h-4 w-4" /> {{ emkgHistoryOpen ? 'Historie ausblenden' : `Historie anzeigen (${emkgAssessments.length - 1})` }}
                </Button>
                <div v-if="emkgHistoryOpen" class="space-y-2 border-t pt-3">
                  <div v-for="assessment in emkgAssessments.slice(1)" :key="assessment.id" class="rounded border bg-white p-3">
                    <div class="mb-2 flex items-center justify-between">
                      <span class="font-medium">{{ assessment.assessed_on }} · {{ assessment.assessor_name || '–' }}</span>
                      <a v-if="assessment.pdf_url" :href="assessment.pdf_url" class="text-blue-700" title="EMKG-PDF herunterladen"><Download class="h-4 w-4" /></a>
                    </div>
                    <div class="flex flex-wrap gap-2">
                      <span v-for="area in emkgAreas" :key="area.key" class="rounded border px-2 py-1 text-xs" :class="emkgLevelClass(assessment.snapshot.effective[area.key].level)" :title="assessment.snapshot.effective[area.key].reason || ''">
                        {{ area.label }}: {{ assessment.snapshot.effective[area.key].level }}<span v-if="assessment.snapshot.effective[area.key].manual">*</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-else-if="selectedGefahrstoff?.gbu?.gbu_url"
                class="flex items-center justify-between gap-3 rounded-md border bg-slate-50 p-3"
              >
                <div>
                  <div class="font-semibold text-gray-800">Hochgeladene Gefährdungsbeurteilung</div>
                  <div class="text-xs text-gray-500">Das hinterlegte Dokument öffnen oder herunterladen.</div>
                </div>
                <a
                  :href="selectedGefahrstoff.gbu.gbu_url"
                  target="_blank"
                  rel="noopener"
                  download
                  class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-white text-blue-700"
                  title="Gefährdungsbeurteilung herunterladen"
                  aria-label="Gefährdungsbeurteilung herunterladen"
                >
                  <Download class="h-4 w-4" />
                </a>
              </div>
              <p v-else class="text-gray-500">Keine EMKG-Bewertung gespeichert.</p>
            </section>

            <!-- Section 6: Betriebsanweisung -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Betriebsanweisung
              </h3>
              <div
                v-if="selectedGefahrstoff?.betriebsanweisung"
                class="grid grid-cols-2 gap-x-6 gap-y-3 text-gray-900"
              >
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Nächste Prüfung</div>
                  <div>{{ selectedGefahrstoff.betriebsanweisung.next_check ? selectedGefahrstoff.betriebsanweisung.next_check.split('T')[0] : '–' }}</div>
                </div>
                <div class="flex flex-col">
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Verantwortlich</div>
                  <div>{{ selectedGefahrstoff.betriebsanweisung.name_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Verantwortlicher</div>
                  <div>{{ selectedGefahrstoff.betriebsanweisung.email_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Abteilung</div>
                  <div>{{ selectedGefahrstoff.betriebsanweisung.email_department || '–' }}</div>
                </div>
                <div class="flex flex-col col-span-2">
                  <a
                    v-if="selectedGefahrstoff.betriebsanweisung.betriebsanweisung_url"
                    :href="selectedGefahrstoff.betriebsanweisung.betriebsanweisung_url"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline"
                  >
                    Betriebsanweisung öffnen
                  </a>
                  <span v-else class="text-gray-500">Kein Dokument verknüpft.</span>
                </div>
              </div>
              <p v-else class="text-gray-500">Keine Betriebsanweisung hinterlegt.</p>
            </section>
          </div>
        </Card>
      </div>
    </div>

    <Sheet v-model:open="formSheetOpen">
      <SheetContent
        side="right"
        class="z-[2500] max-h-[95vh] w-[min(1120px,96vw)] overflow-y-auto p-6 sm:max-w-none"
        overlay-class="z-[2490]"
        :disable-outside-pointer-events="false"
      >
        <SheetHeader class="mb-4">
          <SheetTitle>{{ formSheetTitle }}</SheetTitle>
        </SheetHeader>
        <GefahrstoffForm
          v-if="formMode"
          :key="formKey"
          embedded
          :id="editingGefahrstoffId"
          :duplicate-from-id="duplicateGefahrstoffId"
          @save-start="handleFormSaveStart"
          @saved="handleFormSaved"
          @save-error="handleFormSaveError"
          @cancel="closeFormSheet"
        />
      </SheetContent>
    </Sheet>
  </div>
</template>
